import { Close, History } from '@mui/icons-material';
import { Box } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import { APPROVAL_TYPES, COMITEE_TYPES } from 'lib/api/approvals/types';
import EnhancedTab, { TabRow } from 'lib/designSystem/TabSimulation';
import { showEntityRecord } from 'lib/functions/utils';
import { Warranty } from 'lib/types/models';
import Comitee from 'pages/Finance/Components/Financing/common/Comittee';
import RiskReviews from 'pages/Finance/Components/Financing/common/Risk';
import React, { useEffect } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { ComiteeTabProvider } from '../../common/Comittee/ComiteeTabProvider';
import EditableSummary from './EditableSummary';
import File from './File';
import Payments from './Payments';
import Summary from './Summary/index';

interface WarrantyDetailDialogProps {
  open: boolean;
  onClose: () => void;
  warranty: Warranty | null;
  refreshWarranty: () => void;
}

const WarrantyDetailDialog: React.FC<WarrantyDetailDialogProps> = ({
  open,
  onClose,
  warranty,
  refreshWarranty,
}) => {
  if (!warranty) return null;
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);

  const showPayments =
    warranty.status === 'ACTIVE' || warranty.status === 'APPROVED';

  const title = warranty.folioId
    ? `Garantía ${warranty.displayId}`
    : `Cotización ${warranty.id}`;

  const handleOpenEntityRecord = () => {
    showEntityRecord(dispatch, {
      type: 'SGRWarrantyRequest',
      id: warranty.id,
    });
  };

  const comiteeTabInfo = {
    requestType: APPROVAL_TYPES.WARRANTY_REQUEST,
    comiteeType: COMITEE_TYPES.RISK,
    requestId: warranty.id,
    comiteeId: warranty.comiteeId,
    canEdit: warranty.status === 'IN_REVIEW' || warranty.status === 'REJECTED',
    isRejected: warranty.status === 'REJECTED',
    isActive: warranty.status === 'ACTIVE',
    refreshRequest: async () => refreshWarranty(),
  };

  const tabs = [
    {
      label: 'Resumen',
      component: isEditing ? (
        <EditableSummary
          warranty={warranty}
          setIsEditing={setIsEditing}
          refreshWarranty={refreshWarranty}
        />
      ) : (
        <Summary
          warranty={warranty}
          setIsEditing={setIsEditing}
          refreshWarranty={refreshWarranty}
        />
      ),
    },
    showPayments && {
      label: 'Pagos',
      component: <Payments warrantyId={warranty.id} />,
    },
    {
      label: 'Aprobación',
      component: (
        <ComiteeTabProvider info={comiteeTabInfo}>
          <Comitee />
        </ComiteeTabProvider>
      ),
    },
    {
      label: 'Riesgo',
      component: (
        <RiskReviews item={warranty} requestTypes={['sgr-warranty-request']} />
      ),
    },
    {
      label: 'Documentos',
      component: <File warranty={warranty} />,
    },
  ].filter(Boolean) as TabRow[];

  useEffect(() => {
    if (!open) setIsEditing(false);
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: 'absolute', top: 10, right: 10 }}
      >
        <Close />
      </IconButton>
      <Box style={{ padding: '0 20px' }}>
        <EnhancedTab
          title={title}
          tabs={tabs}
          record={
            warranty.id && (
              <IconButton
                onClick={handleOpenEntityRecord}
                size="small"
                style={{ marginRight: 2 }}
                title="Ver historial de la entidad"
              >
                <History />
              </IconButton>
            )
          }
        />
      </Box>
    </Dialog>
  );
};

export default WarrantyDetailDialog;
